import { TextField } from "@mui/material";
import { useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useMapsKey } from "./mapsProvider";

// TextField wired to Google Places Autocomplete. Falls back to a plain
// TextField when no Maps key is configured. onPlace fires when the user picks
// a suggestion: { address, latitude, longitude }. onUserEdit fires on typed
// edits ONLY when maps are enabled — callers use it to invalidate stored
// coordinates, which is safe only when the map can re-geocode them.
const AutocompleteField = ({ label, value, onChange, onPlace, onUserEdit }) => {
  const places = useMapsLibrary("places");
  const inputRef = useRef(null);
  const onPlaceRef = useRef(onPlace);
  onPlaceRef.current = onPlace;

  useEffect(() => {
    if (!places || !inputRef.current) {
      return;
    }
    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: ["formatted_address", "geometry"],
    });
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const location = place?.geometry?.location;
      onPlaceRef.current?.({
        address: place?.formatted_address || inputRef.current.value,
        latitude: location ? location.lat() : null,
        longitude: location ? location.lng() : null,
      });
    });
    return () => {
      listener.remove();
    };
  }, [places]);

  return (
    <TextField
      fullWidth
      label={label}
      value={value}
      onChange={(e) => {
        onChange(e);
        if (onUserEdit) {
          onUserEdit(e);
        }
      }}
      inputRef={inputRef}
      placeholder="Start typing an address..."
    />
  );
};

const AddressField = ({ label, value, onChange, onPlace, onUserEdit }) => {
  const mapsKey = useMapsKey();

  if (!mapsKey) {
    return <TextField fullWidth label={label} value={value} onChange={onChange} />;
  }

  return (
    <AutocompleteField
      label={label}
      value={value}
      onChange={onChange}
      onPlace={onPlace}
      onUserEdit={onUserEdit}
    />
  );
};

export default AddressField;
